import React, { useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import toast from "react-hot-toast";
import { useApproveFuelRefillMutation, useRejectFuelRefillMutation } from "../fuelApi";
import { PaymentModeBadge, formatDate, money } from "../fuelFormat";

/**
 * Approve or reject SEVERAL submitted refills in one go (2026-09-30, "select the
 * rows in the list and on one click the selected rows get approved or rejected").
 * The month's top-up sheet arrives as one refill per card, so the approver ticks
 * the sheet's rows and decides them together with one comment or one reason.
 *
 * There is no bulk endpoint: each refill goes through its own approve/reject call
 * in turn, so every row is still decided (and its balance moved) by the same
 * server rules as a single decision. A row that fails shows the server's message
 * and stays selectable for another try; the rows that went through are ticked.
 *
 * `vehicleRegsFor` comes from the page (it already joins card -> vehicles) so the
 * list reads like the paper: card, vehicle, amount.
 */
const FuelRefillBulkDecisionModal = ({ show, mode, refills, vehicleRegsFor, onClose, onDone }) => {
  const isApprove = mode === "approve";
  const [comment, setComment] = useState("");
  const [reason, setReason] = useState("");
  const [results, setResults] = useState({}); // recordGuid -> { ok, message }
  const [isSaving, setIsSaving] = useState(false);
  // Snapshot of the selection taken when the modal opens: every approval refreshes the
  // list and un-ticks the decided row, so reading the live selection would make rows
  // vanish from this table while the run is still going.
  const [rows, setRows] = useState([]);
  const [approveRefill] = useApproveFuelRefillMutation();
  const [rejectRefill] = useRejectFuelRefillMutation();

  useEffect(() => {
    if (!show) return;
    setRows(refills || []);
    setComment("");
    setReason("");
    setResults({});
    setIsSaving(false);
  }, [show]); // eslint-disable-line react-hooks/exhaustive-deps
  const pending = rows.filter((r) => !results[r.recordGuid]?.ok);
  const total = pending.reduce((sum, r) => sum + Number(r.refillAmount || 0), 0);
  const doneCount = rows.length - pending.length;
  const canConfirm = pending.length > 0 && !isSaving && (isApprove || reason.trim().length > 0);

  const handleConfirm = async () => {
    setIsSaving(true);
    const next = { ...results };
    const doneGuids = [];
    let okTotal = 0;
    for (const refill of pending) {
      try {
        if (isApprove) {
          // eslint-disable-next-line no-await-in-loop
          await approveRefill({ recordGuid: refill.recordGuid, approvalComment: comment.trim() || null }).unwrap();
        } else {
          // eslint-disable-next-line no-await-in-loop
          await rejectRefill({ recordGuid: refill.recordGuid, rejectionReason: reason.trim() }).unwrap();
        }
        next[refill.recordGuid] = { ok: true };
        doneGuids.push(refill.recordGuid);
        okTotal += Number(refill.refillAmount || 0);
      } catch (error) {
        next[refill.recordGuid] = { ok: false, message: error?.message || "Could not save the decision" };
      }
    }
    setResults(next);
    setIsSaving(false);
    const failCount = pending.length - doneGuids.length;
    if (doneGuids.length > 0) {
      toast.success(
        isApprove
          ? `${doneGuids.length} refill${doneGuids.length === 1 ? "" : "s"} approved - ${money(okTotal)} in total`
          : `${doneGuids.length} refill${doneGuids.length === 1 ? "" : "s"} rejected`
      );
    }
    if (failCount > 0) {
      toast.error(`${failCount} refill${failCount === 1 ? "" : "s"} could not be ${isApprove ? "approved" : "rejected"} - see the rows marked in red`);
      onDone?.(doneGuids);
      return; // stay open so the failures and their reasons are visible
    }
    onDone?.(doneGuids);
    onClose();
  };

  return (
    <Modal show={show} onHide={isSaving ? undefined : onClose} centered backdrop="static" size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {isApprove ? "Approve" : "Reject"} {rows.length} refill{rows.length === 1 ? "" : "s"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="table-responsive mb-3" style={{ maxHeight: "45vh", overflow: "auto" }}>
          <table className="table table-sm align-middle mb-0" style={{ fontSize: 13 }}>
            <thead>
              <tr>
                <th>Refill</th>
                <th>Card</th>
                <th>Vehicle</th>
                <th>Requested by</th>
                <th className="text-end">Amount</th>
                {isApprove && <th className="text-end">Balance after</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const result = results[r.recordGuid];
                const regs = vehicleRegsFor ? vehicleRegsFor(r) : [];
                const isCash = r.paymentMode === "CASH";
                const rowClass = result?.ok ? "table-success" : result ? "table-danger" : "";
                return (
                  <tr key={r.recordGuid} className={rowClass}>
                    <td>
                      <span className="fw-semibold">{r.refillNumber}</span>
                      <div className="small text-muted">{formatDate(r.refillDate)}{r.referenceNumber ? ` · ${r.referenceNumber}` : ""}</div>
                      {result && !result.ok && <div className="small text-danger">{result.message}</div>}
                      {result?.ok && <div className="small text-success">{isApprove ? "approved" : "rejected"} ✓</div>}
                    </td>
                    <td>
                      {isCash ? <PaymentModeBadge mode="CASH" /> : r.cardNumber}
                      <div className="small text-muted">{isCash ? r.branchCode : `${r.provider} · ${r.branchCode}`}</div>
                    </td>
                    <td>{regs.length ? regs.join(", ") : <span className="text-muted">no vehicle</span>}</td>
                    <td>{r.requestedByName || "-"}</td>
                    <td className="text-end fw-semibold text-nowrap">{money(r.refillAmount)}</td>
                    {isApprove && (
                      <td className="text-end text-nowrap text-muted">
                        {isCash ? "-" : money(Number(r.cardCurrentBalance || 0) + Number(r.refillAmount || 0))}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="fw-semibold">
                <td colSpan={4}>
                  {pending.length} to {isApprove ? "approve" : "reject"}
                  {doneCount > 0 && <span className="text-success"> · {doneCount} done</span>}
                </td>
                <td className="text-end text-nowrap">{money(total)}</td>
                {isApprove && <td />}
              </tr>
            </tfoot>
          </table>
        </div>

        {isApprove ? (
          <>
            <div className="alert alert-success py-2 mb-3" style={{ fontSize: 13 }}>
              Approving loads each card amount onto its card straight away (cash rows move no card balance) - <b>{money(total)}</b> in total. Each refill is
              approved on its own; one that the server refuses does not stop the others.
            </div>
            <label className="form-label">Comment <span className="text-muted fw-normal">(same on every refill)</span></label>
            <textarea className="form-control" rows={2} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="optional" disabled={isSaving} />
          </>
        ) : (
          <>
            <label className="form-label">
              Reason <span className="text-danger">*</span> <span className="text-muted fw-normal">(same on every refill)</span>
            </label>
            <textarea
              className="form-control"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why are these being rejected? The requesters see this. No card balance is changed."
              disabled={isSaving}
            />
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={isSaving}>
          {doneCount > 0 ? "Close" : "Cancel"}
        </Button>
        <Button variant={isApprove ? "success" : "danger"} onClick={handleConfirm} disabled={!canConfirm}>
          {isSaving ? "Saving…" : `${isApprove ? "Approve" : "Reject"} ${pending.length} refill${pending.length === 1 ? "" : "s"}`}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default FuelRefillBulkDecisionModal;
